import { Flex } from '@pairy/pds-react';
import { useAtomValue } from 'jotai';
import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import usePaypleScripts from '@/hooks/usePaypleScripts';
import usePayplePayment from '@/hooks/usePayplePayment';
import useDowngradePlan from '@/hooks/mutations/pay/useDowngradePlan';
import { userInfoAtom } from '@/state/user/atom';

import { SelectedPlanAtom } from '../index.atom';
import DowngradeModal from '../DowngradeModal';
import PreventUpgradeModal from '../PreventUpgradeModal';

import * as Styled from './index.styles';

interface Props {
  onOpenChange: (isOpen: boolean) => void;
}
// 결제 관련 로직이 모여있는곳
const UpgradeButton = ({ onOpenChange }: Props) => {
  const queryClient = useQueryClient();
  const selectedPlan = useAtomValue(SelectedPlanAtom);
  const userInfo = useAtomValue(userInfoAtom);

  const [isDowngradeNoticeModalOpen, setIsDowngradeNoticeModalOpen] =
    useState(false);
  const [downgradeExpiredAt, setDowngradeExpiredAt] = useState<string | null>(
    null,
  );

  const [isPreventUpgradeModalOpen, setIsPreventUpgradeModalOpen] =
    useState(false);

  const { PaypleRelatedScripts, isAllScriptsReady } = usePaypleScripts();

  const mutate = usePayplePayment({
    onOpenChange,
    selectedPlanId: selectedPlan?.id,
  });

  const upgradeMutate = usePayplePayment({
    onOpenChange,
    selectedPlanId: selectedPlan?.id,
    isChangePlan: true,
  });

  const { mutate: downgradePlan } = useDowngradePlan({
    onSuccess: (data) => {
      setDowngradeExpiredAt(data.data.data.nextOrderAt);
      setIsDowngradeNoticeModalOpen(true);
    },
    onError: (error) => {
      if (error.response?.status === 400) {
        alert('이미 결제된 플랜입니다');
      } else if (error.response?.status === 403) {
        alert('권한이 없습니다');
      } else if (error.response?.status === 404) {
        alert('구독중인 플랜이 없습니다');
      }
    },
  });

  // case 1. Free plan 유저가 결제하는 경우,
  // payple 모달띄워서 결제 -> pay/cert api 요청
  const handleNewPay = useCallback(() => {
    if (isAllScriptsReady) {
      mutate(undefined);
    }
  }, [isAllScriptsReady, selectedPlan]);

  // case 2. 기존의 결제 유저가 다운그레이드 일 때는,
  // downgrade api 요청
  const handleDowngradePay = () => {
    if (selectedPlan?.id) {
      downgradePlan({ planId: selectedPlan.id });
      return;
    }
    alert('선택된 plan이 없습니다');
  };

  // case 3. 기존의 결제 유저가 업그레이드 일 때는,
  // payple 모달 띄워서 결제 -> pay/upgrade api 요청
  // 단, 연간 유저가 upgrade할 때는 모달 띄워서 막기
  const handleUpgradePay = useCallback(() => {
    upgradeMutate(undefined);
  }, [isAllScriptsReady, selectedPlan]);

  const handlePlanChangeButtonClick = useCallback(() => {
    if (selectedPlan) {
      if (selectedPlan.status === 'new') {
        handleNewPay();
      } else if (selectedPlan.status === 'upgrade') {
        if (userInfo?.subscriptionPlan?.type === 'yearly') {
          setIsPreventUpgradeModalOpen(true);
          return;
        }
        handleUpgradePay();
      } else if (selectedPlan.status === 'downgrade') {
        handleDowngradePay();
      } else {
        alert('이미 적용된 plan입니다');
      }
    }
  }, [isAllScriptsReady, selectedPlan]);

  const handleDowngradeConfirmButtonClick = () => {
    setIsDowngradeNoticeModalOpen(false);
    onOpenChange(false);
    //Downgrade modal이 꺼졌을 때 결제 관리 페이지에서 최신 업데이트 된 값을 보여주기 위해 invalidate처리
    queryClient.invalidateQueries({
      queryKey: ['getSubscriptionAndCardInfo'],
    });
  };

  return (
    <>
      <PaypleRelatedScripts />
      <Flex padding={16} width="100%">
        <Styled.UpgradeButtonWrapper
          width="100%"
          variant="skyblue_500_fill"
          disabled={selectedPlan === null}
          onClick={handlePlanChangeButtonClick}
        >
          플랜 변경하기
        </Styled.UpgradeButtonWrapper>
      </Flex>
      {isDowngradeNoticeModalOpen &&
        selectedPlan &&
        downgradeExpiredAt &&
        userInfo?.subscriptionPlan && (
          <DowngradeModal
            isOpen={isDowngradeNoticeModalOpen}
            setIsOpen={setIsDowngradeNoticeModalOpen}
            nextPlan={selectedPlan.name}
            originPlan={userInfo?.subscriptionPlan?.name}
            expiredAt={downgradeExpiredAt}
            handleConfirmButtonClick={handleDowngradeConfirmButtonClick}
          />
        )}
      {isPreventUpgradeModalOpen && (
        <PreventUpgradeModal
          isOpen={isPreventUpgradeModalOpen}
          setIsOpen={setIsPreventUpgradeModalOpen}
          handleConfirmButtonClick={() => {
            setIsPreventUpgradeModalOpen(false);
            onOpenChange(false);
          }}
        />
      )}
    </>
  );
};

export default UpgradeButton;
